import { Key } from 'react'
import { Button, PopConfirm, Tag } from '@pepper/ui'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { ColumnsType } from 'antd/lib/table'
import { Link } from 'react-router-dom'

import PotentialTrafficTooltipIcon from 'components/idea-lab/PotentialTrafficTooltipIcon'

import { formatLongNumber } from 'utils/functions/formatLongNumber'
import PageUrl from 'container/SEOTools/ContentRefresh/PageUrl'
import type { GapReport } from '@pepper/utils/src/types/seo'

import s from './RecentReportsTable.module.css'

const validPositionArray = [5, 6, 7, 8, 9, 10]

export const getRecentReportsColumn = <RecordType extends GapReport>(
  handleDelete: (reportId: string) => void,
  selectedRowKeys: Key[],
  handleReportClick: (reportId: string, documentId: string) => void
): ColumnsType<RecordType> => {
  return [
    {
      key: 'title',
      title: () => <div className={s.title}>Page Title</div>,
      dataIndex: 'title',
      ellipsis: true,
      render: (_, record) => {
        return (
          <div>
            <div className={s.pageTitle}>{record?.page_title ?? '-'}</div>
            <PageUrl url={record.page_url} />
          </div>
        )
      },
    },
    {
      key: 'target_keyword',
      title: () => <div className={s.title}>Target Keyword</div>,
      dataIndex: 'target_keyword',
      render: (_, record) => {
        return record.target_keyword ? (
          <Tag className={s.targetKeywordTag}>{record.target_keyword}</Tag>
        ) : (
          '-'
        )
      },
    },
    {
      key: 'current_traffic',
      title: () => <div className={s.title}>Current Traffic</div>,
      dataIndex: 'current_traffic',
      width: 120,
      render: (_, record) => {
        return (
          <div>
            {record.traffic ? formatLongNumber(record.traffic, 1) : '-'}
          </div>
        )
      },
    },
    {
      key: 'potential_traffic',
      title: () => (
        <div className={clsx(s.title)}>
          Potential Traffic
          <PotentialTrafficTooltipIcon />
        </div>
      ),
      dataIndex: 'potential_traffic',
      width: 145,
      render: (_, record) =>
        record.potential_traffic ? (
          <div className={s.potentialTraffic}>
            {formatLongNumber(record.potential_traffic, 1)}
            <Icon icon="lucide:arrow-up-right" height={16} />
          </div>
        ) : (
          <div className={s.rightAlign}>-</div>
        ),
    },
    {
      key: 'quick_win',
      title: '',
      dataIndex: 'quick_win',
      width: 110,
      render: (_, record) => {
        const text = validPositionArray.includes(record?.position ?? 0)
          ? 'Quick Win'
          : 'Refresh Opportunity'
        return (
          <div className={s.opportunityLabelWrapper}>
            <div
              title={text}
              className={clsx([s.badge], {
                [s.quickWin]: validPositionArray.includes(
                  record?.position ?? 0
                ),
                [s.refresh]: !validPositionArray.includes(
                  record?.position ?? 0
                ),
              })}
            >
              {text}
            </div>
          </div>
        )
      },
    },
    {
      key: 'view_item',
      title: null,
      dataIndex: 'view_item',
      width: 115,
      render: (_, record) => (
        <div>
          {record.item_id ? (
            <Link
              className={s.link}
              to={
                record?.item_type === 'private_doc'
                  ? `/items/${record?.item_id}`
                  : `/document/${record?.item_id}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              View Item <Icon icon="octicon:chevron-right-16" />
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      key: '',
      title: null,
      dataIndex: '',
      ellipsis: true,
      width: 205,
      render: (_, record, index) => {
        return (
          <div className={s.btnWrapper}>
            <div className={s.actions}>
              {record.generation_status_id === 3 && (
                <>
                  {record.ver === 3 ? (
                    <button
                      className={clsx(s.link, s.btn)}
                      onClick={() =>
                        handleReportClick(record.id, record.document_id)
                      }
                      data-id={`item-${index + 1}-view-gap-report`}
                    >
                      View Report <Icon icon="mingcute:right-line" />
                    </button>
                  ) : (
                    <Link
                      className={s.link}
                      to={`content-refresh/${record.id}/report/${record?.document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-id={`item-${index + 1}-view-gap-report`}
                    >
                      View Report <Icon icon="mingcute:right-line" />
                    </Link>
                  )}
                </>
              )}

              <span className={s.status}>
                {[1, 2].includes(record.generation_status_id ?? 0) && (
                  <span
                    className={s.generatingText}
                    data-id={`item-${index + 1}-loading-gap-report`}
                  ></span>
                )}
                {record.generation_status_id === 4 && 'Failed'}
              </span>

              <PopConfirm
                title="Are you sure about deleting this report?"
                onConfirm={() => {
                  handleDelete(record.id)
                }}
                okText="Delete"
                cancelText="Cancel"
                placement="topRight"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<Icon icon="lucide:trash-2" />}
                  className={s.deleteBtn}
                  disabled={selectedRowKeys.length > 0}
                />
              </PopConfirm>
            </div>
          </div>
        )
      },
    },
  ]
}
